import { Link } from 'react-router'
import MdiChevronRight from '~icons/mdi/chevron-right'
import { MembershipIcon } from 'src/components/Membership/MembershipIcon'
import Text from 'src/components/ui/Text'
import { cx } from 'src/utils/cx'
import styles from './MembershipHeaderBanner.module.css'

/**
 * SSR-safe static visual of `OrdinaryMembershipHeaderBanner`. Mirrors the
 * anonymous default state (no countdown, links to `/membership/plans`,
 * `label_short` copy) so it can be used as the placeholder inside the
 * `app-frame-top-banner` slot without depending on any client-only hooks
 * (`useMyMembership` / `useBreakpoint`).
 *
 * Once `RootRuntimeInner` mounts the real `<AppTopBannerSlot />`, the slot
 * portal swaps this static markup with the live banner. The DOM tree is
 * shaped identically (32px gradient row + crown + label + chevron) so the
 * visual swap is silent even if the user actually qualifies for a different
 * banner state (membership countdown, monthly discount, etc.).
 *
 * NOTE: this module deliberately keeps an SSR-clean import surface — it
 * avoids `useMyMembership` / `useBreakpoint` / `HolidaysEventMembershipTopBanner`
 * etc., which transitively pull in `services/apollo/client` and other
 * client-only modules. This way `AppFrameShell` can import it without
 * polluting the SSR module graph.
 */
export function OrdinaryMembershipHeaderBannerStatic(props: StyleProps) {
  return (
    <div
      className={cx(
        '[--height:32px]',
        'relative z-10 grid h-[--height]',
        props.className,
      )}
      style={props.style}
    >
      <div className={cx(styles['bg'], 'row-start-1 col-start-1')} />
      <Link
        to="/membership/plans"
        className={cx(
          'row-start-1 col-start-1 relative flex justify-center items-center hover:bg-black/10 no-underline transition',
          'text-white font-medium text-shadow shadow-black/50',
        )}
      >
        <MembershipIcon
          className={cx('w-5 h-5 mr-2 -translate-y-px drop-shadow')}
        />
        <span className="leading-[100%] text-center lg:text-left">
          <Text
            i18nKey="membership:membership.banner.label"
            context="short"
            components={{ br: <br />, small: <span className="text-xs" /> }}
          />
        </span>
        <MdiChevronRight className="w-6 h-6 drop-shadow" />
      </Link>
    </div>
  )
}
